/**
 * The story behind Poisson rain: six voices fall as raindrops, each its own
 * Poisson process with its own rate, landing anywhere along the ground. So
 * the drops in any slice follow a Poisson distribution, whether the slice
 * is a second of time or a patch of ground. Each voice plays its own note
 * of a chord, and the chord moves every few seconds, so the rain plays a
 * song. Kept free of React and audio so it can be tested.
 */

/** One voice of the rain: its note in the chord, how often it falls, and its colour. */
export interface RainTone {
  id: string;
  /** Which note of the chord it plays: 0 the root, 1 the third, 2 the fifth. */
  chordNote: 0 | 1 | 2;
  /** True for a voice an octave up, marked with a prime on its chip. */
  isHigh: boolean;
  /** Drops of this voice per second, on average. */
  rate: number;
  /** The drop's colour. */
  color: string;
}

/**
 * The voices, from the most frequent to the rarest: the chord's root,
 * third and fifth, then the same three an octave up. The root falls most
 * often, so the chord is always clear; the high fifth is a rare sparkle.
 */
export const RAIN_TONES: readonly RainTone[] = [
  {id: 'root', chordNote: 0, isHigh: false, rate: 0.5, color: '#3E7CB1'},
  {id: 'third', chordNote: 1, isHigh: false, rate: 0.35, color: '#5A6FD1'},
  {id: 'fifth', chordNote: 2, isHigh: false, rate: 0.3, color: '#7B5CB8'},
  {id: 'high-root', chordNote: 0, isHigh: true, rate: 0.2, color: '#C2549A'},
  {id: 'high-third', chordNote: 1, isHigh: true, rate: 0.12, color: '#C98A12'},
  {id: 'high-fifth', chordNote: 2, isHigh: true, rate: 0.03, color: '#1E9E8A'},
];

/** A chord of the rain's song. */
export type ChordName = 'C' | 'Dm' | 'F' | 'G' | 'Am';

/**
 * Each chord's root, third and fifth, in semitones from middle C. Chords
 * from F up start below middle C, so the song stays in one register.
 */
const CHORD_NOTES: Readonly<
  Record<ChordName, readonly [number, number, number]>
> = {
  C: [0, 4, 7],
  Dm: [2, 5, 9],
  F: [-7, -3, 0],
  G: [-5, -1, 2],
  Am: [-3, 0, 4],
};

/**
 * Where each chord may go next, repeats weighting the likelier moves, as
 * in common voice leading. From Kelly Cochran's Pixel Rain.
 */
const CHORD_MOVES: Readonly<Record<ChordName, readonly ChordName[]>> = {
  C: ['F', 'G', 'Am', 'F', 'G'],
  Dm: ['G', 'G', 'F'],
  F: ['C', 'G', 'Dm', 'C'],
  G: ['C', 'C', 'Am'],
  Am: ['F', 'Dm', 'G'],
};

/** The chord the song starts on. */
export const FIRST_CHORD: ChordName = 'C';

/** How long each chord lasts. */
export const CHORD_SECONDS = 5;

/** Middle C, in Hz. */
const MIDDLE_C = 261.63;

/** Note names by semitone above C; chord notes are all on the white keys. */
const NOTE_NAMES: Readonly<Record<number, string>> = {
  0: 'C',
  2: 'D',
  4: 'E',
  5: 'F',
  7: 'G',
  9: 'A',
  11: 'B',
};

/**
 * Picks the song's next chord.
 *
 * @param params.chord - The chord now.
 * @param params.random - Uniform random numbers in [0, 1).
 * @return The next chord.
 * @throws Never.
 */
export function nextChord({
  chord,
  random,
}: {
  chord: ChordName;
  random: Random;
}): ChordName {
  const moves = CHORD_MOVES[chord];
  return moves[Math.floor(random() * moves.length)] ?? FIRST_CHORD;
}

/** The note a voice plays in one chord. */
export interface ToneNote {
  /** For screen readers and captions, as "E" or "high E". */
  name: string;
  /** The note's letter alone, as "E". */
  letter: string;
  /** For the chip, as "E" or "E′". */
  label: string;
  /** Pitch in Hz. */
  frequency: number;
}

/**
 * The note a voice plays in a chord.
 *
 * @param params.tone - The voice.
 * @param params.chord - The chord.
 * @return Its note's name, chip label and pitch.
 * @throws Never.
 */
export function toneNote({
  tone,
  chord,
}: {
  tone: RainTone;
  chord: ChordName;
}): ToneNote {
  const semitone = CHORD_NOTES[chord][tone.chordNote] + (tone.isHigh ? 12 : 0);
  const letter = NOTE_NAMES[((semitone % 12) + 12) % 12] ?? '?';
  return {
    name: tone.isHigh ? `high ${letter}` : letter,
    letter,
    label: tone.isHigh ? `${letter}\u2032` : letter,
    frequency: MIDDLE_C * 2 ** (semitone / 12),
  };
}

/** Drops of every voice per second, on average: the λ of the whole rain. */
export const TOTAL_RATE = RAIN_TONES.reduce((sum, tone) => sum + tone.rate, 0);

/** How many equal patches the ground is cut into, for counting over space. */
export const PATCHES = 10;

/** How long each patch collects drops before its count is tallied. */
export const ROUND_SECONDS = 10;

/**
 * The expected drops on one patch of ground in one round, for a note (or
 * the whole rain) falling at `rate` drops a second.
 *
 * @param rate - Drops per second across the whole ground.
 * @return λ for one patch: the rate times `ROUND_SECONDS`, shared among
 *     `PATCHES`.
 * @throws Never.
 */
export function patchRate(rate: number): number {
  return (rate * ROUND_SECONDS) / PATCHES;
}

/**
 * The patch a drop lands on.
 *
 * @param x - Where it lands, as a fraction of the ground's width in [0, 1).
 * @return The patch's index in [0, `PATCHES`).
 * @throws Never.
 */
export function patchOf(x: number): number {
  return Math.min(PATCHES - 1, Math.max(0, Math.floor(x * PATCHES)));
}

/** A source of uniform random numbers in [0, 1), like `Math.random`. */
export type Random = () => number;

/**
 * The wait until the next drop of a Poisson process: exponential with the
 * given rate.
 *
 * @param params.rate - Drops per second.
 * @param params.random - Uniform random numbers in [0, 1).
 * @return Seconds until the next drop, always positive.
 * @throws Never.
 */
export function waitForNextDrop({
  rate,
  random,
}: {
  rate: number;
  random: Random;
}): number {
  return -Math.log(1 - random()) / rate;
}

/**
 * The Poisson probability of exactly `k` events when `rate` are expected.
 *
 * @param params.k - The count, a whole number of at least 0.
 * @param params.rate - The expected count, λ; 0 means no events ever.
 * @return P(K = k) for K ~ Poisson(λ).
 * @throws Never.
 */
export function poissonProbability({
  k,
  rate,
}: {
  k: number;
  rate: number;
}): number {
  if (rate <= 0) {
    return k === 0 ? 1 : 0;
  }
  let logFactorial = 0;
  for (let i = 2; i <= k; i += 1) {
    logFactorial += Math.log(i);
  }
  return Math.exp(k * Math.log(rate) - rate - logFactorial);
}

/**
 * How many drops of each note landed in one slice, by tone id. A slice is
 * one second of time, or one patch of ground over one round.
 */
export type SliceCounts = Readonly<Record<string, number>>;

/**
 * The drops counted in one slice, for one note or for all of them.
 *
 * @param params.slice - That slice's counts by tone id.
 * @param params.toneId - One note's id. Omitted means every note together.
 * @return The count.
 * @throws Never.
 */
export function dropsIn({
  slice,
  toneId,
}: {
  slice: SliceCounts;
  toneId?: string;
}): number {
  if (toneId !== undefined) {
    return slice[toneId] ?? 0;
  }
  return Object.values(slice).reduce((sum, count) => sum + count, 0);
}

/** One bar of the chart and the Poisson prediction beside it. */
export interface HistogramBin {
  /** Drops in a slice. */
  k: number;
  /** The share of the counted slices that had exactly k drops. */
  observed: number;
  /** What Poisson(λ) predicts for that share. */
  expected: number;
}

/**
 * The chart: for each count k from 0 to `maxK`, the share of slices with
 * exactly k drops, beside the Poisson prediction.
 *
 * @param params.slices - The counted slices.
 * @param params.rate - The λ to predict with.
 * @param params.maxK - The largest count shown; slices with more drops
 *     count toward no bar.
 * @param params.toneId - Count one note. Omitted means every note.
 * @return One bin per k in [0, maxK].
 * @throws Never.
 */
export function histogram({
  slices,
  rate,
  maxK,
  toneId,
}: {
  slices: readonly SliceCounts[];
  rate: number;
  maxK: number;
  toneId?: string;
}): HistogramBin[] {
  const tally = new Array<number>(maxK + 1).fill(0);
  for (const slice of slices) {
    const count = dropsIn({slice, toneId});
    if (count <= maxK) {
      tally[count] = (tally[count] ?? 0) + 1;
    }
  }
  return tally.map((times, k) => ({
    k,
    observed: slices.length === 0 ? 0 : times / slices.length,
    expected: poissonProbability({k, rate}),
  }));
}

/**
 * The largest count worth a bar for a given λ: about four standard
 * deviations above it, and at least 4.
 *
 * @param rate - The expected count, λ.
 * @return The largest k to chart.
 * @throws Never.
 */
export function chartMaxK(rate: number): number {
  return Math.max(4, Math.ceil(rate + 4 * Math.sqrt(rate)));
}
